import { MesfModal } from "@dexteel/mesf-core";
import {
	Box,
	Button,
	CircularProgress,
	Grid2,
	Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FixedSizeList, ListChildComponentProps } from "react-window";
import { Roll } from "../../models/Roll";
import { rollStatusCodes } from "../RollStatusSelector";
import {
	INITIAL_ROLL_FILTERS,
	RollFilters,
	RollFiltersState,
} from "./RollFilters";
import { RollListItem } from "./RollListItem";

const isCompatibleWithSection = (roll: Roll, sectionId?: number) => {
	if (!sectionId) return false;

	if (roll.RollTypeCode === "E") {
		return roll.Passes?.some((pass) =>
			pass.Sections?.some((section) => section.SectionId === sectionId),
		);
	}

	return roll?.SectionsId?.split(",")?.some(
		(section) => Number(section) === sectionId,
	);
};

const isCompatibleWithStand = (roll: Roll, standId?: number) =>
	standId && roll.StandId === standId;

const ITEM_HEIGHT = 72;
const DEBOUNCE_MS = 1000;
const LIST_OVERHEAD_PX = 360;
const MIN_LIST_HEIGHT_PX = 250;

type Props = {
	open: boolean;
	onClose: () => void;
	onSelect: (roll: Roll | null) => void;
	rolls: Roll[];
	isLoading?: boolean;
	title?: string;
	filterRolls?: "T" | "B" | "A";
	selectedRollId?: number | null;
	standId?: number;
	standName?: string;
	sectionId?: number;
	sectionName?: string;
};

export const RollSelectorDialog = ({
	open,
	onClose,
	onSelect,
	rolls,
	isLoading = false,
	title = "SELECT ROLL",
	filterRolls,
	selectedRollId,
	standId,
	sectionId,
}: Props) => {
	const listRef = useRef<FixedSizeList>(null);

	const [filters, setFilters] =
		useState<RollFiltersState>(INITIAL_ROLL_FILTERS);
	const [debouncedFilters, setDebouncedFilters] =
		useState<RollFiltersState>(INITIAL_ROLL_FILTERS);
	const [localSelectedRoll, setLocalSelectedRoll] = useState<Roll | null>(null);
	const [viewportHeight, setViewportHeight] = useState(() =>
		typeof window !== "undefined" ? window.innerHeight : 800,
	);

	const diameterBounds = useMemo(() => {
		const diameters = rolls
			.map((r) => r.Diameter)
			.filter((d): d is number => d !== null);
		if (diameters.length === 0) return undefined;
		return {
			min: Math.floor(Math.min(...diameters) * 1000) / 1000,
			max: Math.ceil(Math.max(...diameters) * 1000) / 1000,
		};
	}, [rolls]);

	const filteredRolls = useMemo(() => {
		let result = rolls;

		if (filterRolls) {
			result = result.filter(
				(roll) =>
					roll.RollPosition === "A" || roll.RollPosition === filterRolls,
			);
		}

		if (debouncedFilters.search) {
			const searchLower = debouncedFilters.search.toLowerCase();
			result = result.filter((roll) =>
				roll.PartName.toLowerCase().includes(searchLower),
			);
		}

		if (debouncedFilters.type) {
			result = result.filter(
				(roll) => roll.RollTypeCode === debouncedFilters.type,
			);
		}

		if (debouncedFilters.statuses.length > 0) {
			result = result.filter((roll) =>
				debouncedFilters.statuses.includes(roll.RollStatusCode),
			);
		}

		if (debouncedFilters.position) {
			result = result.filter(
				(roll) =>
					roll.RollPosition === "A" ||
					roll.RollPosition === debouncedFilters.position,
			);
		}

		if (debouncedFilters.diameterRange && diameterBounds) {
			const [min, max] = debouncedFilters.diameterRange;
			if (min !== diameterBounds.min || max !== diameterBounds.max) {
				result = result.filter(
					(roll) =>
						roll.Diameter !== null &&
						roll.Diameter >= min &&
						roll.Diameter <= max,
				);
			}
		}

		if (debouncedFilters.inRollset) {
			result = result.filter((roll) => roll.RollsetId !== null);
		}

		if (debouncedFilters.inCarrier) {
			result = result.filter(
				(roll) =>
					roll.RollStatusCode === "A" ||
					roll.RollStatusCode === "S" ||
					roll.RollStatusCode === "C" ||
					roll.RollStatusCode === "I",
			);
		}

		if (debouncedFilters.compatibleWithStand && standId) {
			result = result.filter((roll) => roll.StandId === standId);
		}

		if (debouncedFilters.compatibleWithSection && sectionId) {
			result = result.filter((roll) =>
				isCompatibleWithSection(roll, sectionId),
			);
		}

		const sortedResult = [...result];
		switch (debouncedFilters.sortBy) {
			case "name":
				sortedResult.sort((a, b) => a.PartName.localeCompare(b.PartName));
				break;
			case "diameter":
				sortedResult.sort((a, b) => {
					if (a.Diameter === null && b.Diameter === null) return 0;
					if (a.Diameter === null) return 1;
					if (b.Diameter === null) return -1;
					return a.Diameter - b.Diameter;
				});
				break;
			case "status":
				sortedResult.sort((a, b) => {
					const orderA =
						rollStatusCodes.find((s) => s.Code === a.RollStatusCode)?.order ??
						99;
					const orderB =
						rollStatusCodes.find((s) => s.Code === b.RollStatusCode)?.order ??
						99;
					return orderA - orderB;
				});
				break;
			case "compatibility":
			default:
				sortedResult.sort((a, b) => {
					const aStand = isCompatibleWithStand(a, standId);
					const aSection = isCompatibleWithSection(a, sectionId);
					const bStand = isCompatibleWithStand(b, standId);
					const bSection = isCompatibleWithSection(b, sectionId);

					// Calculate priority: both=0, stand=1, section=2, none=3
					const getPriority = (stand: boolean, section: boolean) => {
						if (stand && section) return 0;
						if (section) return 1;
						if (stand) return 2;
						return 3;
					};

					const priorityA = getPriority(!!aStand, !!aSection);
					const priorityB = getPriority(!!bStand, !!bSection);

					if (priorityA !== priorityB) {
						return priorityA - priorityB;
					}

					return a.PartName.localeCompare(b.PartName);
				});
				break;
		}

		if (debouncedFilters.sortDirection === "desc") {
			sortedResult.reverse();
		}

		return sortedResult;
	}, [
		rolls,
		debouncedFilters,
		filterRolls,
		diameterBounds,
		standId,
		sectionId,
	]);

	const handleSelect = () => {
		onSelect(localSelectedRoll);
		handleClose();
	};

	const handleClose = () => {
		setLocalSelectedRoll(null);
		setFilters(INITIAL_ROLL_FILTERS);
		setDebouncedFilters(INITIAL_ROLL_FILTERS);
		onClose();
	};

	const handleRollClick = useCallback((roll: Roll) => {
		setLocalSelectedRoll(roll);
	}, []);

	const scrollToSelectedRoll = useCallback(() => {
		if (!localSelectedRoll) return;
		const index = filteredRolls.findIndex(
			(r) => r.PartId === localSelectedRoll.PartId,
		);
		if (index >= 0) {
			listRef.current?.scrollToItem(index, "center");
		}
	}, [localSelectedRoll, filteredRolls]);

	useEffect(() => {
		if (open && selectedRollId && rolls.length > 0) {
			const currentRoll = rolls.find((r) => r.PartId === selectedRollId);
			if (currentRoll) {
				setLocalSelectedRoll(currentRoll);
				const index = filteredRolls.findIndex(
					(r) => r.PartId === selectedRollId,
				);
				if (index >= 0) {
					setTimeout(() => listRef.current?.scrollToItem(index, "center"), 100);
				}
			}
		}
	}, [open, selectedRollId, rolls, filteredRolls]);

	useEffect(() => {
		const timer = setTimeout(() => setDebouncedFilters(filters), DEBOUNCE_MS);
		return () => clearTimeout(timer);
	}, [filters]);

	useEffect(() => {
		const handler = () => setViewportHeight(window.innerHeight);
		window.addEventListener("resize", handler);
		return () => window.removeEventListener("resize", handler);
	}, []);

	const maxListHeight = Math.max(
		viewportHeight - LIST_OVERHEAD_PX,
		MIN_LIST_HEIGHT_PX,
	);
	const listHeight =
		isLoading || filteredRolls.length === 0
			? MIN_LIST_HEIGHT_PX
			: Math.min(filteredRolls.length * ITEM_HEIGHT, maxListHeight);

	const Row = useCallback(
		({ index, style }: ListChildComponentProps) => {
			const roll = filteredRolls[index];
			return (
				<div
					style={style}
					data-test-id={`roll-card-${roll.PartName.toLowerCase()}`}
				>
					<RollListItem
						roll={roll}
						selected={localSelectedRoll?.PartId === roll.PartId}
						onClick={() => handleRollClick(roll)}
						standId={standId}
						sectionId={sectionId}
					/>
				</div>
			);
		},
		[filteredRolls, localSelectedRoll, handleRollClick],
	);

	return (
		<MesfModal
			open={open}
			handleClose={handleClose}
			title={title}
			maxWidth="md"
		>
			<MesfModal.Content
				dividers
				sx={{
					padding: 2,
					minWidth: 600,
					maxHeight: "calc(100vh - 100px)",
					display: "flex",
					flexDirection: "column",
					overflow: "hidden",
				}}
			>
				<Box sx={{ flexShrink: 0 }}>
					<RollFilters
						filters={filters}
						onChange={setFilters}
						diameterBounds={diameterBounds}
						standId={standId}
						sectionId={sectionId}
					/>
				</Box>

				<Box
					sx={{
						height: listHeight,
						flexShrink: 0,
						border: "1px solid",
						borderColor: "divider",
						borderRadius: 1,
						overflow: "hidden",
					}}
				>
					{isLoading ? (
						<Box
							sx={{
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
								height: "100%",
							}}
						>
							<CircularProgress />
						</Box>
					) : filteredRolls.length === 0 ? (
						<Box
							sx={{
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
								height: "100%",
								color: "text.secondary",
							}}
						>
							<Typography>No rolls found matching the filters</Typography>
						</Box>
					) : (
						<FixedSizeList
							ref={listRef}
							height={listHeight}
							width="100%"
							itemCount={filteredRolls.length}
							itemSize={ITEM_HEIGHT}
							overscanCount={5}
						>
							{Row}
						</FixedSizeList>
					)}
				</Box>

				<Box
					sx={{
						flexShrink: 0,
						mt: 1,
						p: 1,
						backgroundColor: localSelectedRoll ? "primary.light" : "grey.300",
						borderRadius: 1,
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
					}}
				>
					{localSelectedRoll ? (
						<>
							<Typography variant="body2" color="primary.contrastText">
								Selected: <strong>{localSelectedRoll.PartName}</strong>
							</Typography>
							<Button
								size="small"
								variant="contained"
								color="primary"
								onClick={scrollToSelectedRoll}
								sx={{ fontSize: 10, m: 0 }}
							>
								See roll on list
							</Button>
						</>
					) : (
						<Typography variant="body2" color="text.secondary">
							No roll selected
						</Typography>
					)}
				</Box>
			</MesfModal.Content>

			<MesfModal.Actions style={{ padding: "15px 30px" }}>
				<Grid2 container spacing={2} justifyContent="flex-end">
					<Grid2 size={{ md: 3 }}>
						<Button
							variant="contained"
							color="inherit"
							onClick={handleClose}
							fullWidth
						>
							Cancel
						</Button>
					</Grid2>
					<Grid2 size={{ md: 3 }}>
						<Button
							variant="contained"
							color="primary"
							onClick={handleSelect}
							disabled={!localSelectedRoll}
							fullWidth
							data-test-id="roll-selector-select-button"
						>
							Select
						</Button>
					</Grid2>
				</Grid2>
			</MesfModal.Actions>
		</MesfModal>
	);
};
